import { legacyHostName } from '../utils/legacyHostCompat';
import { hostWebApp } from './hostWebApp';
import type { Well } from '../types';
import { BASINS, type Basin } from '../types';
import { getLastKnownFix, requestPreciseLocation, isGeolocationPermissionDeniedReason } from './highPrecisionLocation';
import { getRuntimeCapabilities, isIosNativeAppRuntime, openExternalUrl } from './runtimeCapabilities';

export interface RouteInfo {
  distance: string;
  duration: string;
  summary: string;
}

export interface Coordinates {
  lat: number;
  lng: number;
}

export interface GoogleMapsDirectionsOptions {
  destination: Coordinates;
  waypoints?: Coordinates[];
  label?: string;
}

export interface NavigationHistoryEntry {
  wellId?: string;
  api?: string;
  name?: string;
  latitude?: number;
  longitude?: number;
  timestamp?: string;
}

export interface NavigationLocationResult {
  location: Coordinates | null;
  stale: boolean;
  reason?: string;
}

// Track if geolocation was denied to avoid repeated permission prompts/errors
let geolocationDenied = false;
let lastOpenMapsFallback: string | null = null;

const NAVIGATION_HISTORY_KEY = 'navigation_history';

const buildGoogleMapsDirectionsUrl = ({ destination, waypoints }: GoogleMapsDirectionsOptions): string => {
  const params = new URLSearchParams({
    api: '1',
    destination: `${destination.lat},${destination.lng}`,
    travelmode: 'driving',
    dir_action: 'navigate',
  });

  if (waypoints && waypoints.length > 0) {
    params.set('waypoints', waypoints.map(w => `${w.lat},${w.lng}`).join('|'));
  }

  return `https://www.google.com/maps/dir/?${params.toString()}`;
};

export function buildAppleMapsDirectionsUrl(lat: number, lng: number): string {
  const query = `daddr=${lat},${lng}&dirflg=d`;
  // Native iOS: maps:// hands off to Apple Maps instead of opening maps.apple.com in Safari.
  if (isIosNativeAppRuntime()) {
    return `maps://?${query}`;
  }
  return `https://maps.apple.com/?${query}`;
}

const readHistory = (raw: string | null | undefined): NavigationHistoryEntry[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export class NavigationService {
  // Use environment variable - must be configured in .env as VITE_GOOGLE_MAPS_API_KEY
  private static readonly GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

  /**
   * Opens Google Maps with driving directions to the well
   * Uses deep link to launch native Google Maps app
   */
  static openGoogleMaps(well: Well) {
    const { latitude, longitude } = well;

    this.openGoogleMapsDirections({
      destination: { lat: latitude, lng: longitude },
      label: well.wellName || well.api || 'Facility',
    });

    // Log navigation
    this.logNavigation(well);
  }

  static openGoogleMapsDirections(options: GoogleMapsDirectionsOptions) {
    const mapsUrl = buildGoogleMapsDirectionsUrl(options);
    const result = openExternalUrl(mapsUrl);
    lastOpenMapsFallback = result.ok ? null : mapsUrl;
    if (result.ok) {
      hostWebApp.hapticFeedback('success');
    } else {
      hostWebApp.hapticFeedback('warning');
      console.warn('[NavigationService] Google Maps popup blocked:', result.fallbackReason);
    }
    return result;
  }

  static getLastOpenMapsFallback(): string | null {
    return lastOpenMapsFallback;
  }

  static clearLastOpenMapsFallback() {
    lastOpenMapsFallback = null;
  }

  /**
   * Opens Google Maps with a waypoint (e.g., route starting point) before the destination
   * Useful for caliche road navigation where you need to get to the main road first
   */
  static openGoogleMapsWithWaypoint(
    waypoint: Coordinates,
    destination: Coordinates
  ) {
    return this.openGoogleMapsDirections({
      destination,
      waypoints: [waypoint],
      label: 'Route waypoint',
    });
  }

  /**
   * Opens Google Maps with multiple waypoints for caliche road navigation
   * Routes via pavement first, then follows the landmark/caliche route
   * Google Maps supports max 9 waypoints - we auto-simplify longer routes
   */
  static openGoogleMapsWithWaypoints(
    waypoints: Coordinates[],
    destination: Coordinates
  ) {
    // Google Maps URL supports max 9 waypoints
    const MAX_WAYPOINTS = 9;
    
    // If too many waypoints, simplify by keeping most important turns
    let routeWaypoints = waypoints;
    if (waypoints.length > MAX_WAYPOINTS) {
      routeWaypoints = this.simplifyWaypoints(waypoints, MAX_WAYPOINTS);
      console.log(`[Navigation] Simplified ${waypoints.length} waypoints to ${routeWaypoints.length}`);
    }
    
    return this.openGoogleMapsDirections({
      destination,
      waypoints: routeWaypoints,
      label: 'Caliche route',
    });
  }

  /**
   * Simplify waypoints by keeping the most important ones (turns, not straight stretches)
   * Uses distance-based selection to keep points that deviate most from straight line
   */
  private static simplifyWaypoints(waypoints: Coordinates[], maxPoints: number): Coordinates[] {
    if (waypoints.length <= maxPoints) return waypoints;
    
    // Always keep first and last waypoints
    const result: Coordinates[] = [waypoints[0]];
    const remaining = waypoints.slice(1, -1);
    const lastWaypoint = waypoints[waypoints.length - 1];
    
    // Calculate importance score for each middle waypoint based on angle deviation
    const scoredWaypoints = remaining.map((wp, i) => {
      const prev = i === 0 ? waypoints[0] : remaining[i - 1];
      const next = i === remaining.length - 1 ? lastWaypoint : remaining[i + 1];
      
      // Calculate angle at this point - sharper angles = more important
      const angle1 = Math.atan2(wp.lat - prev.lat, wp.lng - prev.lng);
      const angle2 = Math.atan2(next.lat - wp.lat, next.lng - wp.lng);
      const angleDiff = Math.abs(angle1 - angle2);
      
      return { waypoint: wp, score: angleDiff, originalIndex: i };
    });
    
    // Sort by importance (angle deviation) and keep top N
    const pointsNeeded = maxPoints - 2; // Account for first and last
    const topWaypoints = scoredWaypoints
      .sort((a, b) => b.score - a.score)
      .slice(0, pointsNeeded)
      .sort((a, b) => a.originalIndex - b.originalIndex) // Restore original order
      .map(item => item.waypoint);
    
    result.push(...topWaypoints, lastWaypoint);
    return result;
  }

  /**
   * Gets route preview information using Google Directions API
   */
  static async getRoutePreview(
    origin: Coordinates,
    destination: Coordinates
  ): Promise<RouteInfo | null> {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/directions/json?` +
        `origin=${origin.lat},${origin.lng}&` +
        `destination=${destination.lat},${destination.lng}&` +
        `mode=driving&` +
        `key=${this.GOOGLE_MAPS_API_KEY}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch route');
      }

      const data = await response.json();

      if (data.status !== 'OK' || !data.routes.length) {
        return null;
      }

      const route = data.routes[0];
      const leg = route.legs[0];

      return {
        distance: leg.distance.text,
        duration: leg.duration.text,
        summary: route.summary || 'Via highways',
      };
    } catch (error) {
      console.error('Route preview error:', error);
      return null;
    }
  }

  /**
   * Gets current user location
   * Use browser geolocation with a cleaner permission prompt
   */
  static resetGeolocationDenied() {
    geolocationDenied = false;
  }

  static getChromeLocationHelpText(): string {
    return 'Allow location for ziawellnav.com in Chrome site settings.';
  }

  static getBrowserLocationHelpText(): string {
    const capabilities = getRuntimeCapabilities();
    if (capabilities.mode === 'safari-browser') {
      return 'Allow location for ziawellnav.com in Safari (Websites → Location) and enable Location Services for Safari.';
    }
    if (capabilities.mode === 'chrome-browser') {
      return this.getChromeLocationHelpText();
    }
    return 'Allow location for ziawellnav.com in your browser site settings.';
  }

  static async getCurrentLocationWithStatus(options: { explicitRetry?: boolean; allowLastKnownFallback?: boolean } = {}): Promise<NavigationLocationResult> {
    // Skip the host location API when it would show a disruptive prompt
    // Use browser geolocation directly for cleaner system UI
    if (options.explicitRetry) {
      this.resetGeolocationDenied();
    }
    
    // Skip if previously denied (avoid spam)
    if (geolocationDenied) {
      console.log('[NavigationService] Browser geolocation previously denied, skipping');
      return {
        location: null,
        stale: false,
        reason: this.getBrowserLocationHelpText(),
      };
    }

    console.log('[NavigationService] Requesting high-precision location...');
    const result = await requestPreciseLocation({
      desiredAccuracyMeters: 20,
      initialTimeoutMs: 15000,
      refinementTimeoutMs: 6000,
      allowLastKnownFallback: options.allowLastKnownFallback ?? false,
      allowHostLocationFallback: false,
    });

    if (!result.fix) {
      if (result.reason && isGeolocationPermissionDeniedReason(result.reason)) {
        geolocationDenied = true;
      }
      console.log('[NavigationService] High-precision location failed:', result.reason);
      if (options.allowLastKnownFallback) {
        const lastKnown = getLastKnownFix(10 * 60 * 1000);
        if (lastKnown) {
          return {
            location: { lat: lastKnown.lat, lng: lastKnown.lng },
            stale: true,
            reason: 'Using estimated last known location.',
          };
        }
      }
      const capabilities = getRuntimeCapabilities();
      return {
        location: null,
        stale: false,
        reason: (capabilities.mode === 'chrome-browser' || capabilities.mode === 'safari-browser')
          ? this.getBrowserLocationHelpText()
          : result.reason,
      };
    }

    geolocationDenied = false;
    return {
      location: {
        lat: result.fix.lat,
        lng: result.fix.lng,
      },
      stale: result.usedFallback,
      reason: result.usedFallback ? result.reason : undefined,
    };
  }

  static async getCurrentLocation(): Promise<Coordinates | null> {
    const result = await this.getCurrentLocationWithStatus();
    return result.location;
  }

  /**
   * Calculates distance between two coordinates (Haversine formula)
   */
  static calculateDistance(coord1: Coordinates, coord2: Coordinates): number {
    const R = 3959; // Earth's radius in miles
    const dLat = this.toRad(coord2.lat - coord1.lat);
    const dLng = this.toRad(coord2.lng - coord1.lng);
    
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(coord1.lat)) *
        Math.cos(this.toRad(coord2.lat)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private static toRad(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Shows confirmation dialog before navigating
   */
  static async confirmNavigation(well: Well, routeInfo?: RouteInfo | null): Promise<boolean> {
    return new Promise((resolve) => {
      let message = `Navigate to ${well.wellName}?`;
      
      if (routeInfo) {
        message += `\n\n📍 Distance: ${routeInfo.distance}\n⏱️ Time: ${routeInfo.duration}`;
      }

      hostWebApp.showConfirm(message, (confirmed: boolean) => {
        resolve(confirmed);
      });
    });
  }

  static async confirmNavigationTarget(
    target: { name?: string; wellName?: string; api?: string },
    routeInfo?: RouteInfo | null
  ): Promise<boolean> {
    return new Promise((resolve) => {
      const label = target.wellName || target.name || target.api || 'this location';
      let message = `Navigate to ${label}?`;
      if (routeInfo) {
        message += `\n\nDistance: ${routeInfo.distance}\nTime: ${routeInfo.duration}`;
      }
      hostWebApp.showConfirm(message, (confirmed: boolean) => resolve(confirmed));
    });
  }

  /**
   * Shows popup prompting user to download offline maps
   */
  static promptOfflineMapDownload() {
    // Use confirm dialogs instead of showPopup for better compatibility
    
    // First, ask which basin
    const message = 
      '🗺️ Download Offline Maps\n\n' +
      'Select a basin:\n\n' +
      '1️⃣ Permian Basin (Southeast NM)\n' +
      '2️⃣ San Juan Basin (Northwest NM)\n' +
      '3️⃣ Raton Basin (Northeast NM)\n\n' +
      'Reply with the number (1, 2, or 3):';
    
    hostWebApp.showAlert(message);
    
    // Show buttons using the host button system
    if (hostWebApp.webApp.showPopup) {
      hostWebApp.webApp.showPopup({
        title: '🗺️ Select Basin',
        message: 'Choose which basin to download:',
        buttons: [
          { id: 'permian', type: 'default', text: 'Permian' },
          { id: 'san-juan', type: 'default', text: 'San Juan' },
          { id: 'raton', type: 'default', text: 'Raton' },
          { id: 'cancel', type: 'cancel', text: 'Cancel' }
        ]
      }, (buttonId: string) => {
        if (buttonId && buttonId !== 'cancel') {
          this.downloadOfflineMap(buttonId as Basin);
        }
      });
    } else {
      // Fallback: use confirm dialogs
      setTimeout(() => {
        hostWebApp.showConfirm('Download Permian Basin?', (yes: boolean) => {
          if (yes) {
            this.downloadOfflineMap('permian');
          } else {
            hostWebApp.showConfirm('Download San Juan Basin?', (yes: boolean) => {
              if (yes) {
                this.downloadOfflineMap('san-juan');
              } else {
                hostWebApp.showConfirm('Download Raton Basin?', (yes: boolean) => {
                  if (yes) {
                    this.downloadOfflineMap('raton');
                  }
                });
              }
            });
          }
        });
      }, 500);
    }
  }

  /**
   * Initiates offline map download for specified basin
   */
  static downloadOfflineMap(basin: Basin) {
    const basinInfo = BASINS[basin];
    const { center, bounds } = basinInfo;
    
    // Calculate appropriate zoom level based on bounds
    const latDiff = bounds.north - bounds.south;
    const lngDiff = bounds.east - bounds.west;
    const maxDiff = Math.max(latDiff, lngDiff);
    const zoom = Math.floor(14 - Math.log2(maxDiff));
    
    // Google Maps deep link for offline download
    const androidDeepLink = `geo:${center.lat},${center.lng}?z=${zoom}&q=${encodeURIComponent(basinInfo.name)}`;
    
    if (hostWebApp.isAndroid()) {
      // Android: Open Google Maps directly
      openExternalUrl(androidDeepLink, { tryHostBridge: false });
      
      // Show instructions
      setTimeout(() => {
        hostWebApp.showAlert(
          `📍 Google Maps is Opening\n\n` +
          `To download ${basinInfo.name} offline map:\n\n` +
          `1. When map opens, look at bottom tabs\n` +
          `2. Swipe the tabs LEFT (past Directions, Start, Save, Share, Add label)\n` +
          `3. Tap "More" (three dots)\n` +
          `4. Tap "Download offline map"\n` +
          `5. Adjust the map area to cover ${basinInfo.name}\n` +
          `6. Tap "Download"\n\n` +
          `💾 Usually 50-200 MB per basin\n` +
          `📶 Works without internet!`
        );
      }, 1500);
    } else {
      // iOS or desktop
      const webUrl = `https://www.google.com/maps/@${center.lat},${center.lng},${zoom}z`;
      openExternalUrl(webUrl, { tryHostBridge: false });
      
      hostWebApp.showAlert(
        `🗺️ Download ${basinInfo.name} Offline Map\n\n` +
        `📱 ANDROID - Google Maps:\n` +
        `1. Swipe bottom tabs LEFT (past Directions, Start, Save, Share)\n` +
        `2. Tap "More" (three dots •••)\n` +
        `3. Tap "Download offline map"\n` +
        `4. Adjust map area to cover ${basinInfo.name}\n` +
        `5. Tap "Download"\n\n` +
        `🍎 iOS - Apple Maps:\n` +
        `1. Open Apple Maps\n` +
        `2. Search for "${basinInfo.name}"\n` +
        `3. Swipe up on location card\n` +
        `4. Tap "Download" button\n\n` +
        `💾 Size: 50-200 MB per basin\n` +
        `📶 Works completely offline!`
      );
    }
  }

  static saveNavigationHistory(entry: NavigationHistoryEntry) {
    const nextEntry = {
      ...entry,
      timestamp: entry.timestamp || new Date().toISOString(),
    };
    const capabilities = getRuntimeCapabilities();
    const webApp = (window as any)[legacyHostName()]?.WebApp;

    if (capabilities.canUseHostCloudStorage && webApp?.CloudStorage) {
      try {
        webApp.CloudStorage.getItem(NAVIGATION_HISTORY_KEY, (error: string | null, value?: string) => {
          if (error) return;
          const history = readHistory(value);
          const trimmed = [nextEntry, ...history].slice(0, 50);
          webApp.CloudStorage.setItem(NAVIGATION_HISTORY_KEY, JSON.stringify(trimmed), () => {});
        });
        return;
      } catch (error) {
        console.warn('[NavigationService] CloudStorage navigation history failed, using localStorage', error);
      }
    }

    try {
      const history = readHistory(localStorage.getItem(NAVIGATION_HISTORY_KEY));
      const trimmed = [nextEntry, ...history].slice(0, 50);
      localStorage.setItem(NAVIGATION_HISTORY_KEY, JSON.stringify(trimmed));
    } catch (error) {
      console.warn('[NavigationService] local navigation history failed:', error);
    }
  }

  private static logNavigation(well: Well) {
    this.saveNavigationHistory({
      wellId: well.id,
      api: well.api,
      name: well.wellName,
      latitude: well.latitude,
      longitude: well.longitude,
    });
  }
}
